import React, { useState, useEffect, useMemo } from "react";
import CategoryWheel from "./CategoryWheel";
import PlayerProgress from "./PlayerProgress";
import GoogleAd from "./GoogleAd";
import preguntas from "../data/preguntas330.json";
import temas from "../data/temas_dgt";
import aciertoSound from "../sounds/acierto.mp3";
import errorSound from "../sounds/error.mp3";
import "./Game.css";

function getTemasPendientes(jugador) {
  return temas.filter(t => (jugador.progreso?.[t] || 0) < 4);
}
function getTemasCompletados(jugador) {
  return temas.filter(t => (jugador.progreso?.[t] || 0) >= 4);
}
function getTemasCompletos10(jugador) {
  return temas.filter(t => (jugador.progreso?.[t] || 0) >= 4).length >= 10;
}
const INICIAL_HISTORIAL = () => ({
  preguntasRespondidas: [],
});

function Game({ jugadores, dificultadSeleccionada, onExit }) {
  const jugadoresLimpio = (jugadores || []).map(j => ({
    ...j,
    nombre: j.nombre || "Jugador",
    progreso: j.progreso || {},
    historial: j.historial || [],
    ...INICIAL_HISTORIAL()
  }));

  const [players, setPlayers] = useState(jugadoresLimpio);
  const [turno, setTurno] = useState(0);
  const [categoriaActual, setCategoriaActual] = useState(null);
  const [preguntaActual, setPreguntaActual] = useState(null);
  const [opcionSeleccionada, setOpcionSeleccionada] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [bloquear, setBloquear] = useState(false);
  const [estado, setEstado] = useState("ruleta");

  const preguntasPorTema = useMemo(() => {
    const agrupadas = {};
    temas.forEach(t => {
      agrupadas[t] = preguntas.filter(q => q.tema === t);
    });
    return agrupadas;
  }, []);

  const unicoJugador = players.length === 1;
  const jugadorActual = players[turno] || { nombre: "..." };

  const ganador = players.find(getTemasCompletos10);

  useEffect(() => {
    if (ganador) setEstado("fin");
    // eslint-disable-next-line
  }, [players, ganador]);

  function siguienteTurno() {
    setTurno(prev => (unicoJugador ? 0 : (prev + 1) % players.length));
    setCategoriaActual(null);
    setPreguntaActual(null);
    setOpcionSeleccionada("");
    setMensaje("");
    setEstado("ruleta");
    setBloquear(false);
  }

  function handleCategoriaSeleccionada(cat) {
    setCategoriaActual(cat);
    let pool = preguntasPorTema[cat];
    if (dificultadSeleccionada && dificultadSeleccionada !== "aleatorio") {
      pool = pool.filter(q => q.nivel === dificultadSeleccionada);
    }
    pool = pool.filter(
      q => !(jugadorActual.historial || []).includes(q.id)
    );
    if (pool.length === 0) {
      setMensaje("No hay más preguntas disponibles en este tema y dificultad.");
      setTimeout(siguienteTurno, 1600);
      return;
    }
    const pregunta = pool[Math.floor(Math.random() * pool.length)];
    setPreguntaActual(pregunta);
    setEstado("pregunta");
    setMensaje("");
  }

  function handleOpcionSeleccionada(opcion) {
    setOpcionSeleccionada(opcion);
    setMensaje("");
  }

  function handleConfirmar() {
    if (!preguntaActual || !opcionSeleccionada) {
      setMensaje("Selecciona una opción antes de confirmar.");
      return;
    }
    setBloquear(true);
    const esCorrecta = opcionSeleccionada === preguntaActual.correcta;
    const audio = new Audio(esCorrecta ? aciertoSound : errorSound);
    audio.play();

    setPlayers(players => {
      return players.map((j, idx) => {
        if (idx !== turno) return j;
        const nuevoProgreso = { ...j.progreso };
        if (esCorrecta) {
          nuevoProgreso[categoriaActual] = (nuevoProgreso[categoriaActual] || 0) + 1;
        }
        const nuevoHistorial = [...(j.historial || []), preguntaActual.id];
        return {
          ...j,
          progreso: nuevoProgreso,
          historial: nuevoHistorial,
        };
      });
    });

    setMensaje(
      esCorrecta
        ? "✅ ¡Correcto!"
        : `❌ Incorrecto. La respuesta correcta era: ${preguntaActual.opciones[preguntaActual.correcta]}`
    );
    setEstado("explicacion");
    setTimeout(siguienteTurno, 2100);
  }

  const temasPendientes = getTemasPendientes(jugadorActual);
  const temasCompletados = getTemasCompletados(jugadorActual);

  if (!jugadores || jugadores.length === 0) {
    return (
      <div style={{ textAlign: "center", marginTop: "3rem" }}>
        <h2>No hay jugadores en la partida.</h2>
        <button className="btn-volver" onClick={onExit}>Volver</button>
      </div>
    );
  }

  if (estado === "fin" && ganador) {
    const empate = players.filter(getTemasCompletos10).length > 1;
    return (
      <div className="game-container anim-fadein">
        <h2 className="win-title">🏆 ¡{empate ? "Empate" : ganador.nombre + " gana"}!</h2>
        <p className="win-msg">
          {empate
            ? "Varios jugadores han completado 10 temas a la vez."
            : `${ganador.nombre} ha completado 10 temas.`}
        </p>
        <div className="players-final-list">
          {players.map((j, idx) => (
            <div key={idx} className="player-final-card">
              <div className="player-final-nombre">{j.nombre}</div>
              <PlayerProgress progreso={j.progreso} temas={temas} temasCompletados={getTemasCompletados(j)} />
              <div className="temas-completados">
                Temas completados: <b>{getTemasCompletados(j).length}</b>
              </div>
            </div>
          ))}
        </div>
        <button className="btn-volver" onClick={onExit}>🔄 Volver al inicio</button>
        <GoogleAd />
      </div>
    );
  }

  return (
    <div className="game-container anim-fadein">
      <div className="top-panel">
        <span className="turno-label">
          {unicoJugador ? "Tu progreso" : `Turno de: ${jugadorActual.nombre}`}
        </span>
        <button className="btn-volver-top" onClick={onExit} title="Salir al inicio">
          ⬅️
        </button>
      </div>

      <PlayerProgress
        progreso={jugadorActual.progreso}
        temas={temas}
        temasCompletados={temasCompletados}
      />

      <div className="temas-restantes">
        Temas restantes: <b>{temasPendientes.length}</b> / 12
      </div>

      {estado === "ruleta" && (
        <CategoryWheel
          categoriasPendientes={temasPendientes}
          onSelect={handleCategoriaSeleccionada}
          disabled={bloquear || temasPendientes.length === 0}
        />
      )}

      {estado === "pregunta" && preguntaActual && (
        <div className="pregunta-card anim-fadein">
          <h3 className="pregunta-title">{preguntaActual.pregunta}</h3>
          <div className="opciones-list">
            {Object.entries(preguntaActual.opciones).map(([letra, texto]) => (
              <button
                key={letra}
                className={`opcion-btn ${opcionSeleccionada === letra ? "selected" : ""}`}
                onClick={() => handleOpcionSeleccionada(letra)}
                disabled={bloquear}
              >
                <b>{letra}.</b> {texto}
              </button>
            ))}
          </div>
          <button
            className="btn-confirmar"
            onClick={handleConfirmar}
            disabled={bloquear}
          >
            Confirmar respuesta
          </button>
        </div>
      )}

      {estado === "explicacion" && preguntaActual && (
        <div className="explicacion-card anim-fadein">
          <div className="explicacion-txt">
            <b>Explicación:</b> {preguntaActual.explicacion}
          </div>
          <div className="mensaje-txt">{mensaje}</div>
        </div>
      )}

      <GoogleAd />
    </div>
  );
}

export default Game;
